import Axios from "axios";
import { AddressType, Status, GeocodingAddressComponentType, PlaceType2 } from "@googlemaps/google-maps-services-js";
const independentCities = {
  "VA": [
    "Alexandria",
    "Bristol",
    "Buena Vista",
    "Charlottesville",
    "Chesapeake",
    "Colonial Heights",
    "Covington",
    "Danville",
    "Emporia",
    "Fairfax",
    "Falls Church",
    "Franklin",
    "Fredericksburg",
    "Galax",
    "Hampton",
    "Harrisonburg",
    "Hopewell",
    "Lexington",
    "Lynchburg",
    "Manassas",
    "Manassas Park",
    "Martinsville",
    "Newport News",
    "Norfolk",
    "Norton",
    "Petersburg",
    "Poquoson",
    "Portsmouth",
    "Radford",
    "Richmond",
    "Roanoke",
    "Salem",
    "Staunton",
    "Suffolk",
    "Virginia Beach",
    "Waynesboro",
    "Williamsburg",
    "Winchester"
  ],
  "MO": ["St. Louis", "Saint Louis"],
  "NV": ["Carson City"],
  "MD": ["Baltimore"]
};
class MissingAddressDetailsError extends Error {
  constructor(missingTypes, message) {
    super(message);
    this.missingTypes = missingTypes;
    this.message = message;
    this.name = "MissingAddressDetailsError";
    Object.setPrototypeOf(this, MissingAddressDetailsError.prototype);
  }
}
function extractCounty(addressComponents, state) {
  var _a, _b;
  let county = (_a = addressComponents.find((component) => component.types.includes(AddressType.administrative_area_level_2) && component.types.includes(AddressType.political))) == null ? void 0 : _a.short_name;
  if (county === void 0) {
    const locality = (_b = addressComponents.find((component) => component.types.includes(AddressType.locality) && component.types.includes(AddressType.political))) == null ? void 0 : _b.short_name;
    if (locality !== void 0 && state !== void 0 && state in independentCities && independentCities[state].map((city) => city.toLocaleLowerCase()).includes(locality.toLocaleLowerCase())) {
      county = locality;
    }
  }
  return county;
}
function extractCity(addressComponents, fullAddress) {
  var _a, _b, _c, _d;
  const parts = [
    (_a = addressComponents.find((component) => component.types.includes(AddressType.locality) && component.types.includes(AddressType.political))) == null ? void 0 : _a.short_name,
    (_b = addressComponents.find((component) => component.types.includes(AddressType.sublocality) && component.types.includes(AddressType.political))) == null ? void 0 : _b.short_name,
    (_c = addressComponents.find((component) => component.types.includes(AddressType.neighborhood) && component.types.includes(AddressType.political))) == null ? void 0 : _c.short_name,
    (_d = addressComponents.find((component) => component.types.includes(AddressType.administrative_area_level_3) && component.types.includes(AddressType.political))) == null ? void 0 : _d.short_name
  ].filter((a) => a);
  const city = extractCityFromFullAddress(fullAddress);
  if (city !== null && parts.includes(city)) {
    return city;
  }
  return parts.length > 0 ? parts[0] : void 0;
}
function extractCityFromFullAddress(fullAddress) {
  const parse = fullAddress.split(", ");
  if (parse.length > 1) {
    const [, city, suffix] = parse;
    if (suffix !== void 0) {
      const [state] = suffix.split(" ");
      if (state.length === 2 && state.toUpperCase() === state) {
        return city;
      }
    }
  }
  return null;
}
function parseGoogleGeoCodeToAddressDetails(googleGeoCode, requiredFields) {
  var _a, _b, _c, _d, _e, _f, _g;
  const addressComponents = googleGeoCode.address_components;
  const country = (_a = addressComponents.find((component) => component.types.includes(AddressType.country) && component.types.includes(AddressType.political))) == null ? void 0 : _a.short_name;
  const state = (_b = addressComponents.find((component) => component.types.includes(AddressType.administrative_area_level_1) && component.types.includes(AddressType.political))) == null ? void 0 : _b.short_name;
  const county = extractCounty(addressComponents, state);
  const city = extractCity(addressComponents, googleGeoCode.formatted_address);
  const street = (_c = addressComponents.find((component) => component.types.includes(AddressType.route))) == null ? void 0 : _c.short_name;
  const streetNumber = (_d = addressComponents.find((component) => component.types.includes(GeocodingAddressComponentType.street_number))) == null ? void 0 : _d.short_name;
  const zip = (_e = addressComponents.find((component) => component.types.includes(AddressType.postal_code))) == null ? void 0 : _e.short_name;
  const zipSuffix = (_f = addressComponents.find((component) => component.types.includes(PlaceType2.postal_code_suffix))) == null ? void 0 : _f.short_name;
  const address2 = (_g = addressComponents.find((component) => component.types.includes(AddressType.subpremise))) == null ? void 0 : _g.short_name;
  const fullAddress = googleGeoCode.formatted_address;
  if (country === void 0 && requiredFields !== void 0 && requiredFields.includes("country")) {
    throw new MissingAddressDetailsError([AddressType.country, AddressType.political], `Missing  ${AddressType.political} and ${AddressType.country} types in address components`);
  }
  if (state === void 0 && requiredFields !== void 0 && requiredFields.includes("state")) {
    throw new MissingAddressDetailsError([AddressType.administrative_area_level_1, AddressType.political], `Missing ${AddressType.political} and  ${AddressType.administrative_area_level_1} types in address components`);
  }
  if (county === void 0 && requiredFields !== void 0 && requiredFields.includes("county")) {
    throw new MissingAddressDetailsError([AddressType.administrative_area_level_2, AddressType.political], `Missing political ${AddressType.political} and  ${AddressType.administrative_area_level_2} types in address components`);
  }
  if (city === void 0 && requiredFields !== void 0 && requiredFields.includes("city")) {
    throw new MissingAddressDetailsError([AddressType.neighborhood, AddressType.political, AddressType.sublocality, AddressType.locality], `Missing ${AddressType.locality}, ${AddressType.sublocality}, ${AddressType.neighborhood} and ${AddressType.sublocality}types in address components`);
  }
  if (street === void 0 && requiredFields !== void 0 && requiredFields.includes("street")) {
    throw new MissingAddressDetailsError([AddressType.route], `Missing ${AddressType.route} type in address components`);
  }
  if (zip === void 0 && requiredFields !== void 0 && requiredFields.includes("zip")) {
    throw new MissingAddressDetailsError([AddressType.postal_code], `Missing ${AddressType.postal_code} type in address components`);
  }
  if (zipSuffix === void 0 && requiredFields !== void 0 && requiredFields.includes("zipSuffix")) {
    throw new MissingAddressDetailsError([PlaceType2.postal_code_suffix], `Missing ${PlaceType2.postal_code_suffix} type in address components`);
  }
  if (address2 === void 0 && requiredFields !== void 0 && requiredFields.includes("address2")) {
    throw new MissingAddressDetailsError([AddressType.subpremise], `Missing ${AddressType.subpremise} type in address components`);
  }
  return {
    streetNumber: streetNumber === void 0 ? null : streetNumber,
    street: street === void 0 ? null : street,
    city: city === void 0 ? null : city,
    county: county === void 0 ? null : county,
    state: state === void 0 ? null : state,
    country: country === void 0 ? null : country,
    zip: zip === void 0 ? null : zip,
    zipSuffix: zipSuffix === void 0 ? null : zipSuffix,
    address2: address2 !== void 0 ? `#${address2}` : null,
    googleGeoCodeResponse: googleGeoCode,
    fullAddress,
    status: googleGeoCode.status,
    location: googleGeoCode.geometry.location
  };
}
const fromGoogleGeoCode = async (googleGeoCodeResponse, options, requiredFields) => {
  try {
    return parseGoogleGeoCodeToAddressDetails(googleGeoCodeResponse, requiredFields);
  } catch (e) {
    if (e instanceof MissingAddressDetailsError) {
      if ((e.missingTypes.includes(AddressType.administrative_area_level_2) || e.missingTypes.includes(AddressType.route)) && (options.mfAutoFix === void 0 || options.mfAutoFix)) {
        const streetNumberRegex = /^[0-9]+\s|^[0-9]+-[0-9]+\s/;
        const modifiedAddressText = googleGeoCodeResponse.formatted_address.replace(streetNumberRegex, "");
        const newGoogleGeoCoderResponse = await getGoogleGeoCodeWithAddress(modifiedAddressText, options);
        return parseGoogleGeoCodeToAddressDetails(newGoogleGeoCoderResponse, requiredFields);
      } else {
        throw e;
      }
    } else {
      throw e;
    }
  }
};
const fromAddressText = async (addressText, options, requiredFields) => {
  let modifiedAddressText = addressText;
  const streetNumbersRegex = /^([0-9]+)(\s)([0-9]+\s)/;
  if (options.mfAutoFix === void 0 || options.mfAutoFix) {
    modifiedAddressText = addressText.replace(streetNumbersRegex, "$1-$3");
  }
  const googleResponse = await getGoogleGeoCodeWithAddress(modifiedAddressText, options);
  return fromGoogleGeoCode(googleResponse, options, requiredFields);
};
const fromPlaceId = async (placeId, options, requiredFields) => {
  const googleResponse = await getGoogleGeoCodeWithPlaceId(placeId, options);
  return fromGoogleGeoCode(googleResponse, options, requiredFields);
};
function toMfAddressComponents(googleAddressComponents) {
  let addressComponentsCountry = null;
  let administrativeAreaLevel1 = null;
  let administrativeAreaLevel2 = null;
  let sublocalityLevel1 = null;
  let neighborhood = null;
  let locality = null;
  googleAddressComponents.googleGeoCodeResponse.address_components.forEach((section) => {
    if (section.types.indexOf("sublocality_level_1") > -1 || section.types.indexOf("sublocality") > -1) {
      sublocalityLevel1 = section.short_name;
    }
    if (section.types.indexOf("locality") > -1) {
      locality = section.short_name;
    }
    if (section.types.indexOf("neighborhood") > -1) {
      neighborhood = section.short_name;
    }
    if (section.types.indexOf("administrative_area_level_1") > -1) {
      administrativeAreaLevel1 = section.short_name;
    }
    if (section.types.indexOf("administrative_area_level_2") > -1) {
      administrativeAreaLevel2 = section.short_name;
    }
    if (section.types.indexOf("country") > -1) {
      addressComponentsCountry = section.short_name;
    }
  });
  if (addressComponentsCountry === null || administrativeAreaLevel1 === null) {
    throw new Error("bad address components!\n" + JSON.stringify(googleAddressComponents.googleGeoCodeResponse.address_components));
  }
  if (googleAddressComponents.country === null || googleAddressComponents.county === null || googleAddressComponents.state === null) {
    throw new Error("Cannot have null address fields: country: " + googleAddressComponents.country + ", state: " + googleAddressComponents.state + ", county: " + googleAddressComponents.county);
  }
  const formatedAddress = {
    location: googleAddressComponents.location,
    address1: (googleAddressComponents.streetNumber === null ? "" : googleAddressComponents.streetNumber + " ") + googleAddressComponents.street,
    address2: googleAddressComponents.address2,
    country: googleAddressComponents.country,
    state: googleAddressComponents.state,
    county: googleAddressComponents.county,
    city: googleAddressComponents.city,
    zip5: googleAddressComponents.zip,
    zip4: googleAddressComponents.zipSuffix,
    street: googleAddressComponents.street,
    streetNumber: googleAddressComponents.streetNumber,
    crossStreet: null,
    fullAddress: googleAddressComponents.fullAddress
  };
  const patientAddressComponents = {
    location: googleAddressComponents.googleGeoCodeResponse.geometry.location,
    country: addressComponentsCountry,
    administrativeAreaLevel1,
    administrativeAreaLevel2: administrativeAreaLevel2 === null ? "" : administrativeAreaLevel2,
    sublocalityLevel1,
    neighborhood,
    locality,
    formatedAddressDetails: formatedAddress
  };
  return patientAddressComponents;
}
async function getGoogleGeoCodeWithAddress(addressText, options) {
  const response = await Axios.get("https://maps.googleapis.com/maps/api/geocode/json?address=" + encodeURIComponent(addressText) + "&key=" + options.apiKey);
  return handleResponse(response);
}
async function getGoogleGeoCodeWithPlaceId(placeId, options) {
  const response = await Axios.get("https://maps.googleapis.com/maps/api/geocode/json?place_id=" + encodeURIComponent(placeId) + "&key=" + options.apiKey);
  return handleResponse(response);
}
async function handleResponse(response) {
  if (response.status < 200 || response.status > 300) {
    throw new Error(`Status code error: ${response.status}`);
  }
  const googleAddressComponents = response.data;
  if (googleAddressComponents.error_message !== void 0) {
    throw new Error(googleAddressComponents.error_message);
  }
  if (googleAddressComponents.results.length === 0) {
    throw new Error(Status.ZERO_RESULTS);
  }
  return {
    address_components: googleAddressComponents.results[0].address_components,
    formatted_address: googleAddressComponents.results[0].formatted_address,
    geometry: googleAddressComponents.results[0].geometry,
    place_id: googleAddressComponents.results[0].place_id,
    types: googleAddressComponents.results[0].types,
    status: googleAddressComponents.status
  };
}
export { MissingAddressDetailsError, fromAddressText, fromGoogleGeoCode, fromPlaceId, toMfAddressComponents };
//# sourceMappingURL=mf-google-geocoder.es.js.map
